/**
 * Download menu for portal exports.
 *
 * These are plain anchors, not fetch+blob. The portal calls the API
 * same-origin, so the browser sends the session cookies and handles the
 * Content-Disposition attachment itself — no token juggling, no object URLs to
 * revoke, and a failed download surfaces as the browser's own error rather
 * than a silently swallowed promise.
 *
 * Links carry the current domain scope so a download matches the view the
 * customer is looking at; omitting it exports the whole organisation.
 */
import { Download } from "lucide-react";
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@ddmarc/ui";
import { usePortalScope } from "@/lib/portal-scope-context";

type ExportKind = "reports" | "senders" | "summary";

const FILES: Record<ExportKind, { path: string; label: string; hint: string }> = {
  reports: {
    path: "reports.csv",
    label: "Report records (CSV)",
    hint: "Every authentication result",
  },
  senders: {
    path: "senders.csv",
    label: "Sending sources (CSV)",
    hint: "One row per source",
  },
  summary: {
    path: "summary.pdf",
    label: "Posture summary (PDF)",
    hint: "Shareable overview",
  },
};

export function ExportMenu({
  kinds = ["reports", "senders", "summary"],
  days = 30,
}: {
  kinds?: ExportKind[];
  days?: number;
}) {
  const { selectedDomainId } = usePortalScope();

  const href = (kind: ExportKind) => {
    const params = new URLSearchParams({ days: String(days) });
    if (selectedDomainId !== null) params.set("domain_id", String(selectedDomainId));
    return `/api/v1/portal/exports/${FILES[kind].path}?${params.toString()}`;
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="sm" className="h-7 gap-1.5 text-[12px]">
          <Download className="h-3.5 w-3.5" />
          Export
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-64">
        <DropdownMenuLabel className="text-[11.5px] font-normal text-muted-foreground">
          Last {days} days
          {selectedDomainId === null ? " · all domains" : " · selected domain"}
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {kinds.map((kind) => (
          <DropdownMenuItem key={kind} asChild>
            {/* download lets the browser keep the server-provided filename */}
            <a href={href(kind)} download className="flex flex-col items-start gap-0.5">
              <span className="text-[12.5px]">{FILES[kind].label}</span>
              <span className="text-[11px] text-muted-foreground">{FILES[kind].hint}</span>
            </a>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
